import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import PropertyGrid from "@/components/PropertyGrid";
import Header from "@/components/Header";
import { properties } from "@/data/properties";
import { usePublishedProperties } from "@/hooks/usePublishedProperties";
import { formatPrice, getPriceAmount } from "@/utils/price";

const typeLabels = {
  economico: "economicos",
  economicos: "economicos",
  local: "locales",
  oficina: "oficinas / consultorios",
  consultorio: "oficinas / consultorios",
  casa: "casas para negocio",
  bodega: "bodegas",
  terreno: "terrenos",
};

const normalize = (text) =>
  (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\btres\b/g, "3")
    .replace(/[-\s]+/g, " ")
    .trim();

const formatLocationName = (location = "") =>
  location
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatPriceRange = (minPrice, maxPrice) => {
  if (minPrice && maxPrice && minPrice === maxPrice) {
    return formatPrice(minPrice);
  }

  if (minPrice && maxPrice) {
    return `${formatPrice(minPrice)} a ${formatPrice(maxPrice)}`;
  }

  if (minPrice) return `desde ${formatPrice(minPrice)}`;
  if (maxPrice) return `hasta ${formatPrice(maxPrice)}`;
  return "";
};

const getImportantLocationTokens = (value = "") =>
  normalize(value)
    .split(" ")
    .filter(
      (token) =>
        token.length > 2 &&
        !["ciudad", "mexico", "estado", "colonia", "municipio", "delegacion"].includes(token)
    );

const locationTextMatches = (searchableLocation = "", requestedLocation = "") => {
  const searchable = normalize(searchableLocation);
  const requested = normalize(requestedLocation);
  if (!requested) return true;
  if (searchable.includes(requested) || requested.includes(searchable)) return true;

  const requestedTokens = getImportantLocationTokens(requested);
  if (!requestedTokens.length) return false;

  return requestedTokens.some((token) => searchable.includes(token));
};

const getLocationSearchText = (property = {}) => {
  const baseText = [
    property.address,
    property.location,
    property.city,
    property.ciudad,
    property.municipio,
    property.alcaldia,
    property.state,
    property.entidad,
    property.zona,
    property.zonaDetalle,
    property.title,
    property.description,
    property.contacto?.ciudad,
    property.contacto?.estado,
  ].filter(Boolean).join(" ");
  const normalizedBase = normalize(baseText);
  const inferredText =
    normalizedBase.includes("auriga") || normalizedBase.includes("coyoacan")
      ? " Coyoacan Ciudad de Mexico CDMX"
      : "";

  return `${baseText}${inferredText}`;
};

const Buscar = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const publishedProperties = usePublishedProperties();
  const allProperties = [...publishedProperties, ...properties];

  const filters = useMemo(() => {
    const params = new URLSearchParams(locationHook.search);

    return {
      type: params.get("type") || "",
      location: params.get("location") || "",
      minPrice: params.get("minPrice") || "",
      maxPrice: params.get("maxPrice") || "",
      customPrice: params.get("customPrice") === "1",
    };
  }, [locationHook.search]);

  const minPrice = Number(filters.minPrice);
  const maxPrice = Number(filters.maxPrice);
  const isEconomicRange = minPrice >= 2000 && maxPrice <= 8000;
  const hideEconomicosCategory = Boolean(maxPrice && maxPrice > 8000);
  const filterType = normalize(filters.type);
  const filterLocation = normalize(filters.location);
  const locationName = formatLocationName(filters.location);
  const priceRangeText = formatPriceRange(minPrice, maxPrice);

  useEffect(() => {
    if (!isEconomicRange) return;

    const params = new URLSearchParams();

    if (filters.location) {
      params.set("location", filters.location);
    }

    navigate(`/economicos?${params.toString()}`, { replace: true });
  }, [filters.location, isEconomicRange, navigate]);

  if (isEconomicRange) return null;

  const matchesLocation = (property) => {
    if (!filterLocation) return true;

    const searchableLocation = normalize(getLocationSearchText(property));

    return searchableLocation.includes(filterLocation);
  };

  const matchesType = (property) => {
    const type = normalize(property.type);

    if (filterType === "oficina") {
      return type === "oficina" || type === "consultorio";
    }

    return filterType ? type === filterType : true;
  };

  const matchesPrice = (property) => {
    const price = getPriceAmount(property);

    return (
      (minPrice ? price >= minPrice : true) &&
      (maxPrice ? price <= maxPrice : true)
    );
  };

  const filteredProperties = allProperties.filter(
    (property) =>
      matchesLocation(property) &&
      matchesType(property) &&
      matchesPrice(property)
  );
  const locationMatches = filterLocation
    ? allProperties.filter(matchesLocation)
    : [];
  const nearbyPriceProperties =
    filteredProperties.length === 0 && filters.customPrice && minPrice
      ? allProperties
          .filter((property) => {
            if (!matchesLocation(property) || !matchesType(property)) return false;

            const price = getPriceAmount(property);
            return price >= Math.max(0, minPrice - 2000) && price <= minPrice + 2000;
          })
          .sort(
            (a, b) =>
              Math.abs(getPriceAmount(a) - minPrice) -
              Math.abs(getPriceAmount(b) - minPrice)
          )
      : [];
  const zoneAlternativeProperties =
    filteredProperties.length === 0 && !filters.customPrice && locationMatches.length > 0
      ? [...locationMatches].sort((a, b) => getPriceAmount(a) - getPriceAmount(b))
      : [];
  const alternativeProperties = nearbyPriceProperties.length
    ? nearbyPriceProperties
    : zoneAlternativeProperties;
  const showingAlternatives = alternativeProperties.length > 0;
  const sortedProperties = [
    ...(showingAlternatives ? alternativeProperties : filteredProperties),
  ].sort((a, b) => getPriceAmount(a) - getPriceAmount(b));
  const availableTypes = [
    ...new Set(
      alternativeProperties
        .map(
          (property) =>
            typeLabels[normalize(property.type)] || normalize(property.type)
        )
        .filter(Boolean)
    ),
  ];
  const resultTitle = showingAlternatives
    ? filters.customPrice
      ? `Precios cercanos a ${priceRangeText}`
      : `Opciones disponibles en ${locationName}`
    : "Resultados";
  const resultSubtitle = showingAlternatives
    ? filters.customPrice
      ? "No hubo coincidencia exacta, pero estas opciones estan cerca del precio que buscaste"
      : "No hubo coincidencia exacta, pero estas opciones estan en la misma zona"
    : "Propiedades encontradas";

  return (
    <>
    <Header />
    <div className="w-full pt-20">
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Volver al inicio"
        className="fixed right-4 top-20 z-40 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-black text-slate-600 shadow-sm"
      >
        X
      </button>
      <div className="max-w-7xl mx-auto px-4">
        {showingAlternatives && (
          <div className="mb-5 rounded-2xl border border-orange-100 bg-orange-50 p-5 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">
              {filters.customPrice
                ? `No tenemos exactamente propiedades de ${priceRangeText}`
                : `No tenemos exactamente lo que buscaste en ${locationName}`}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {filters.customPrice
                ? `No hay propiedades exactamente en ${priceRangeText}`
                : priceRangeText
                ? `No hay propiedades en el rango ${priceRangeText}`
                : "No hay propiedades con esos filtros"}
              {filters.type ? ` para ${typeLabels[filterType] || filters.type}` : ""}
              , pero si tenemos {alternativeProperties.length} opcion(es)
              {filters.customPrice ? " cercanas al precio." : " en esa zona."}
            </p>
            {availableTypes.length > 0 && (
              <p className="mt-2 text-sm font-semibold text-slate-800">
                Tipos disponibles: {availableTypes.join(", ")}.
              </p>
            )}
          </div>
        )}

        <PropertyGrid
          properties={sortedProperties}
          loading={false}
          selectedCategory={filters.type}
          showCategories={false}
          onlyEconomicos={false}
          hideEconomicos={hideEconomicosCategory}
          showRibbon={false}
          onCategoryChange={() => {}}
          onPropertyClick={(property) =>
            navigate(`/${property.type}/${property.id}`)
          }
          title={resultTitle}
          subtitle={resultSubtitle}
        />
      </div>
    </div>
    </>
  );
};

export default Buscar;
